import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Container,
  Typography,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Check as CheckIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { selectCalculatedPremium, selectMaxCoveredValue } from '../context/util/selectors';

import InfoboxSchutz from '../components/InfoboxSchutz';
import './TarifBerechnen.css';
import { calculateEarliestStartOfInsurance, findBestMatchingProduct, formatCurrency } from '../util/helper';
import { useYupValidation } from '../hooks/useYupValidation';
import * as yup from 'yup';
import { isValid, startOfDay } from 'date-fns';
import NumberField from '../components/form/NumberField';
import { deDE } from '@mui/x-date-pickers/locales';
import GlobalErrorAlert from '../components/GlobalErrorAlert';
import { useGlobalError } from '../context/GlobalErrorContext';

const paymentPeriods = [
  { label: 'monatlich', code: 'MONTHLY_CONTINUOUS', factor: 1 },
  { label: 'vierteljährlich', code: 'QUARTERLY_CONTINUOUS', factor: 3 },
  { label: 'halbjährlich', code: 'SIX_MONTH_CONTINUOUS', factor: 6 },
  { label: 'jährlich', code: 'ANNUALLY_CONTINUOUS', factor: 12 },
];

const TarifBerechnen = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); /* war: md */
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); /* try: md */

  const { state, dispatch } = useAppContext();
  const { clearGlobalError } = useGlobalError();

  const maxCoveredValue = useMemo(() => {
    return selectMaxCoveredValue(state);
  }, [state]);

  const validationRules = useMemo(() => {
    return yup.object().shape({
      versicherungssumme: yup
        .number()
        // handle empty string as null, instead of NaN
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .required('Bitte Kaufpreis angeben')
        .min(100, 'Mindestversicherungssumme: 100 €')
        .max(maxCoveredValue, `Maximale Versicherungssumme: ${formatCurrency(maxCoveredValue)}`),

      zahlungsintervall: yup
        .string()
        .required('Bitte Zahlungsintervall wählen')
        .oneOf(
          ['MONTHLY_CONTINUOUS', 'QUARTERLY_CONTINUOUS', 'SIX_MONTH_CONTINUOUS', 'ANNUALLY_CONTINUOUS'],
          'Ungültiges Zahlungsintervall',
        ),

      versicherungsbeginn: yup
        .date()
        .typeError('Das eingegebene Datum muss dem Format TT.MM.JJJJ entsprechen')
        .required('Das eingegebene Datum muss dem Format TT.MM.JJJJ entsprechen')
        .test('min-date', 'Versicherungsbeginn darf nicht in der Vergangenheit liegen', value => {
          return value && startOfDay(value) >= startOfDay(new Date());
        }),
    });
  }, [maxCoveredValue]);

  const [availableOptions, setAvailableOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ...state.formData,
    versicherungssumme: Number(state.formData.versicherungssumme) || null,
    versicherungsbeginn: state.formData.versicherungsbeginn ?? calculateEarliestStartOfInsurance(),
    zahlungsintervall: state.formData.zahlungsintervall || 'MONTHLY_CONTINUOUS',
    selectedOptions: state.formData.selectedOptions || [],
  });

  const { errors, fieldRefs, validateField, validateForm } = useYupValidation(validationRules);
  const [calculatedPremium, setCalculatedPremium] = useState(0);
  const selectedPaymentPeriod = paymentPeriods.find(period => period.code === formData.zahlungsintervall);
  const paymentIntervalFactor = selectedPaymentPeriod?.factor ?? 1;
  const paymentIntervalLabel = selectedPaymentPeriod?.label ?? 'monatlich';

  // global State updaten, wenn sich local formData ändert
  useEffect(() => {
    dispatch({ type: 'SET_FORM_DATA', payload: formData });
  }, [formData, dispatch]);

  // Produktauswahl und verfügbare Optionen auf Basis des eingegebenen Kaufpreises
  useEffect(() => {
    const versicherungssumme = formData.versicherungssumme;
    const allProductsAndOptions = state.configuration.products;

    const selectedProduct = findBestMatchingProduct(allProductsAndOptions, versicherungssumme);
    if (selectedProduct === null) {
      return;
    }

    selectedProduct.scope = [
      {
        label: 'GENERIC',
        value: versicherungssumme,
      },
    ];

    dispatch({ type: 'SET_SELECTED_PRODUCT', payload: selectedProduct });
    setAvailableOptions(selectedProduct.options || []);
  }, [formData.versicherungssumme, state.configuration, dispatch]);

  const calculatePremium = useCallback(() => {
    const premium = selectCalculatedPremium(state);
    setCalculatedPremium(premium);
  }, [state]);

  // Premiumberechnung anstoßen und verfügbare Optionen anpassen, wenn sich Produktauswahl ändert
  useEffect(() => {
    setAvailableOptions(state.selectedProduct?.options || []);
    calculatePremium();
  }, [state.selectedProduct, calculatePremium]);

  const handleInputChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = date => {
    setFormData(prev => ({ ...prev, versicherungsbeginn: date }));
    if (isValid(date)) {
      validateField('versicherungsbeginn', date);
    }
  };

  const handleOptionToggle = optionId => {
    setFormData(prev => ({
      ...prev,
      selectedOptions: prev.selectedOptions.includes(optionId)
        ? prev.selectedOptions.filter(id => id !== optionId)
        : [...prev.selectedOptions, optionId],
    }));
  };

  const handleBlur = async event => {
    const { name, value } = event.target;
    await validateField(name, value);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    clearGlobalError();

    if (await validateForm(formData)) {
      setLoading(true);

      try {
        const enrichedFormData = {
          ...formData,
          calculatedPremium,
          sessionId: `session_${Date.now()}`,
        };

        dispatch({ type: 'SET_FORM_DATA', payload: enrichedFormData });
        dispatch({ type: 'SET_CURRENT_STEP', payload: 2 });
        navigate('/persoenliche-angaben');
      } catch (error) {
        console.error('Fehler beim Laden der Produkte:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box className="wrapper" role="main" aria-label="Tarifrechner für Hardware-Versicherung">
        <Container className="main-content-scroll-container">
          <Box className="main-content-with-sidebar">
            {/* --- PAGE CONTENT  --- */}
            <Box className="page-content">
              <Box
                component="form"
                name="tarifberechnung"
                className="tarif-berechnen-form-container"
                role="form"
                onSubmit={handleSubmit}
                noValidate
                aria-label="Tarifberechnung"
              >
                {/* Überschrift */}
                <Typography variant="h1" component="h1" className="headline-large-bold">
                  Tarif berechnen
                </Typography>

                <p style={{ marginBottom: '20px' }}> Mit * gekennzeichnete Felder sind Pflichtfelder. </p>

                <GlobalErrorAlert sx={{ mb: 1 }} />

                <Typography
                  variant="h2"
                  component="h2"
                  id="jumpmark-maincontent"
                  tabIndex={-1}
                  sx={{ outline: 'none' }}
                >
                  Kaufpreis deiner Hardware
                </Typography>

                {/* Eingabebereich */}
                <Box className="insurance-amount-container">
                  <Grid container spacing={0} className="insurance-amount-row">
                    <Grid item xs={8}>
                      <NumberField
                        id="versicherungssumme"
                        inputRef={fieldRefs.versicherungssumme}
                        fullWidth
                        name="versicherungssumme"
                        value={formData.versicherungssumme}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        required
                        label="Kaufpreis in EUR"
                        variant="filled"
                        type="text"
                        className="input-field larger-input-text"
                        error={!!errors.versicherungssumme}
                        helperText={errors.versicherungssumme || 'z.B. 1200,00'}
                        slotProps={{
                          formHelperText: { 'data-testid': 'versicherungssumme-error' },
                        }}
                        sx={{
                          '&:focus-visible': {
                            border: '2px solid var(--color-secondary) !important',
                            borderRadius: '4px !important',
                          },
                          '& .MuiInputBase-root:focus-visible': {
                            border: '2px solid var(--color-secondary) !important',
                            borderRadius: '4px !important',
                          },
                          '& .MuiInputBase-root.Mui-focused': {
                            border: '2px solid var(--color-secondary) !important',
                            borderRadius: '4px !important',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Box className="price-display-container">
                        <Typography
                          variant="body1"
                          component="span"
                          align="right"
                          className="h1-style"
                          data-testid="grundpreis"
                        >
                          {formData.versicherungssumme ? '' : 'ab'}{' '}
                          {formatCurrency((state.selectedProduct?.premium?.gross ?? 0) * paymentIntervalFactor)}
                        </Typography>
                        <Typography variant="body2" component="span" align="right" className="text-blue">
                          {paymentIntervalLabel}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Hinweistext */}
                <Box className="info-text-container">
                  <Typography component="p" variant="body1">
                    Die R+V Hardwareversicherung bietet Versicherungsschutz für eine Vielzahl von Schadenereignissen.
                    Manche Schadenereignisse sind u. U. bereits in einer bestehenden Hausratversicherung versichert.
                    Prüfe daher eine mögliche Doppelversicherung.
                  </Typography>
                </Box>

                {/* "Das ist versichert" Box */}
                <Box className="insured-items-box">
                  <Paper className="insured-items-paper" role="region" aria-label="Das ist versichert: Auflistung">
                    <Typography variant="h2" component="h2" className="margin-bottom-16">
                      Das ist versichert:
                    </Typography>

                    <Stack spacing={1.25}>
                      {[
                        'PCs, Laptops, Spielkonsolen, Monitore/Fernseher',
                        'Originalzubehör und nachgerüstetes Zubehör',
                        'Peripheriegeräte wie z.B. Gamepads, Mäuse, Tastaturen, Headsets oder VR-Brillen',
                        'Streaming Setup, professionelle Beleuchtung und Gaming Chairs',
                        'Gaming-Equipment, das bei Abschluss der Versicherung nicht älter als 36 Monate und im Schadenfall nicht älter als 60 Monate ab Neukauf ist',
                      ].map((item, index) => (
                        <Box key={index} className="insured-item-container">
                          <CheckIcon className="insured-item-icon" />
                          <Typography variant="body1" component="p" className="insured-item-text text-20-normal">
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>

                {/* Ausschlüsse */}
                <Box className="info-text-container">
                  <Typography variant="body1" component="p">
                    Nicht versichert sind Handys, Smartphones, Tablets, Smartwatches, Ausstellungsgeräte, reimportierte
                    Geräte sowie Schäden durch Witterungseinflüsse und elementare Naturereignisse.
                  </Typography>
                </Box>

                {/* Zusatzoptionen Überschrift */}
                <Typography variant="h2" component="h2" className="options-title">
                  Mach deine Hardware noch sicherer!
                </Typography>

                {/* Zusatzoptionen WCAG-konform*/}
                <Box className="options-container" role="group" aria-label="Auswahl von Zusatzoptionen">
                  <Stack spacing={1.25}>
                    {availableOptions.map((option, index) => (
                      <ButtonBase
                        key={index}
                        className="option-paper"
                        onClick={() => handleOptionToggle(option.code)}
                        role="checkbox"
                        aria-checked={formData.selectedOptions.includes(option.code)}
                        sx={{
                          '&:focus-visible': {
                            border: '2px solid var(--color-primary-text)!important',
                          },
                        }}
                      >
                        <Box className="option-container">
                          <Box className="option-checkbox-container">
                            {formData.selectedOptions.includes(option.code) ? (
                              <CheckBoxIcon className="option-checkbox-checked" aria-hidden="true" />
                            ) : (
                              <CheckBoxOutlineBlankIcon className="option-checkbox-unchecked" aria-hidden="true" />
                            )}
                          </Box>
                          <Box className="option-content">
                            <Box className="flex-row-between">
                              <Typography variant="h3" component="h3" className="option-title">
                                {option.label}
                              </Typography>
                              <Typography
                                variant="h3"
                                component="h3"
                                className="option-price option-price-desktop"
                                data-testid={`option-price-${option.code}`}
                              >
                                {formData.versicherungssumme ? '+' : 'ab'}{' '}
                                {formatCurrency(option.premium.gross * paymentIntervalFactor)}
                              </Typography>
                            </Box>
                            <Typography variant="body1" component="p" className="option-description">
                              {option.description}
                            </Typography>
                            <Typography variant="h3" component="h3" className="option-price option-price-mobile">
                              {formData.versicherungssumme ? '+' : 'ab'}{' '}
                              {formatCurrency(option.premium.gross * paymentIntervalFactor)}
                            </Typography>
                          </Box>
                        </Box>
                      </ButtonBase>
                    ))}
                  </Stack>
                </Box>

                {/* Zahlungsintervall */}
                <Box className="payment-interval-container">
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      <Typography variant="h2" className="payment-interval-title">
                        Deine Zahlungsweise
                      </Typography>
                    </FormLabel>
                    <RadioGroup
                      id="zahlungsintervall-group"
                      name="zahlungsintervall"
                      value={formData.zahlungsintervall}
                      onChange={handleInputChange}
                      row={false}
                      className="radio-group"
                      data-testid="zahlungsintervall-radiogroup"
                    >
                      {!isMobile ? (
                        <Grid container>
                          <Grid item xs={6} className="payment-interval-options-grid-cell">
                            <div className="payment-interval-options-container">
                              {paymentPeriods.slice(0, Math.ceil(paymentPeriods.length / 2)).map(period => (
                                <FormControlLabel
                                  key={period.code}
                                  value={period.code}
                                  control={
                                    <Radio
                                      slotProps={{
                                        input: {
                                          id: `zahlungsintervall-input-${period.code}`,
                                          ref: fieldRefs.zahlungsintervall,
                                        },
                                      }}
                                    />
                                  }
                                  label={period.label}
                                  className="radio-option"
                                  variant="body2"
                                  component="span"
                                />
                              ))}
                            </div>
                          </Grid>
                          <Grid item xs={6} className="payment-interval-options-grid-cell">
                            <div className="payment-interval-options-container">
                              {paymentPeriods.slice(Math.ceil(paymentPeriods.length / 2)).map(period => (
                                <FormControlLabel
                                  key={period.code}
                                  value={period.code}
                                  control={
                                    <Radio
                                      slotProps={{
                                        input: {
                                          id: `zahlungsintervall-input-${period.code}`,
                                          ref: fieldRefs.zahlungsintervall,
                                        },
                                      }}
                                    />
                                  }
                                  label={period.label}
                                  className="radio-option"
                                  variant="body2"
                                  component="span"
                                />
                              ))}
                            </div>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container>
                          <Grid item xs={12}>
                            <div className="payment-interval-options-container">
                              {paymentPeriods.map(period => (
                                <FormControlLabel
                                  key={period.code}
                                  value={period.code}
                                  control={
                                    <Radio
                                      slotProps={{
                                        input: {
                                          id: `zahlungsintervall-input-${period.code}`,
                                          ref: fieldRefs.zahlungsintervall,
                                        },
                                      }}
                                    />
                                  }
                                  label={period.label}
                                  className="radio-option"
                                  variant="body2"
                                  component="span"
                                />
                              ))}
                            </div>
                          </Grid>
                        </Grid>
                      )}
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Versicherungsbeginn Überschrift */}
                <Typography variant="h2" component="h2" className="options-title">
                  Dein Versicherungsbeginn
                </Typography>

                <Grid item xs={12}>
                  {/* Versicherungsbeginn */}
                  <Box className="date-picker-container">
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={de}
                      localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
                    >
                      <DatePicker
                        label="Versicherungsbeginn"
                        value={formData.versicherungsbeginn}
                        onChange={handleDateChange}
                        format="dd.MM.yyyy"
                        disablePast={true}
                        sx={{
                          '& .MuiIconButton-root:focus-visible': {
                            boxShadow: '0 0 0 2px var(--color-secondary)',
                            borderRadius: '2px', // Definiert die Eckigkeit
                          },
                          // Border immer mit --color-secondary
                          '& .MuiPickersOutlinedInput-notchedOutline': {
                            borderColor: 'var(--color-secondary) !important',
                          },
                          // Input-Feld Focus Outline
                          '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                            border: '2px solid var(--color-secondary) !important',
                          },
                        }}
                        slotProps={{
                          textField: {
                            id: 'versicherungsbeginn',
                            inputRef: fieldRefs.versicherungsbeginn,
                            required: true,
                            fullWidth: true,
                            variant: 'outlined',
                            className: 'date-input',
                            error: !!errors.versicherungsbeginn,
                            helperText: errors.versicherungsbeginn || '',
                            onBlur: () => {
                              validateField('versicherungsbeginn', formData.versicherungsbeginn);
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  {/* Hinweis Mindestvertragslaufzeit */}
                  <Box className="info-text-container">
                    <Typography component="p" variant="body1">
                      Die Mindestvertragslaufzeit beträgt 1 Jahr und kann zum Ende der Mindestvertragslaufzeit mit einer
                      Frist von einem Monat erstmalig gekündigt werden. Nach der Mindestvertragslaufzeit kann der
                      Vertrag monatlich mit einer Frist von einem Monat gekündigt werden.
                    </Typography>
                  </Box>

                  {/* Sticky InfoboxSchutz für Desktop */}
                  {isDesktop && (
                    <div className="infobox-sidebar-container">
                      <InfoboxSchutz />
                    </div>
                  )}
                </Grid>

                {/* Weiter-Button */}
                <Box className="button-container" sx={{ mt: 3, mb: 2 }}>
                  <Button
                    className="button-general button-action"
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    aria-label="Persönliche Daten angeben"
                  >
                    Persönliche Daten angeben
                  </Button>
                </Box>
              </Box>
            </Box>{' '}
            {/* className="page-content" */}
            {/* --- ENDE PAGE CONTENT  --- */}
          </Box>{' '}
          {/* className="main-content-with-sidebar" */}
        </Container>{' '}
        {/* className="main-content-scroll-container" */}
      </Box>{' '}
      {/* className="wrapper" */}
    </LocalizationProvider>
  );
};

export default TarifBerechnen;
